import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../utils/api";
import Icon from "./common/icons";
import { UserContext } from "../context/UserContext";
import "../styles/SectionUpdateModal.css";

// Students pick from the academic departments only; "other" is staff (Personnel Type)
const STUDENT_DEPARTMENT_TYPES = ["basic_education", "higher_education", "graduate_school"];
// The list shows this many matches; typing narrows it down
const LIST_LIMIT = 60;

/**
 * Every section a student can pick, flattened from the department → level → section
 * options (/auth/user-organizational-info/), staff groups left out. Exported for tests.
 */
export const sectionChoices = (available) => {
  if (!available) return [];
  const levelsFor = (deptId) =>
    available.organizational_levels?.[deptId] || available.organizational_levels?.[Number(deptId)] || [];
  const sectionsFor = (levelId) =>
    available.sections?.[levelId] || available.sections?.[Number(levelId)] || [];
  const choices = [];
  (available.departments || [])
    .filter((dept) => STUDENT_DEPARTMENT_TYPES.includes(dept.type))
    .forEach((dept) => {
      levelsFor(dept.id).forEach((level) => {
        sectionsFor(level.id).forEach((section) => {
          choices.push({
            id: String(section.id),
            name: section.name,
            where: [dept.name, level.name].filter(Boolean).join(" · "),
            search: `${section.name} ${level.name} ${dept.name}`.toLowerCase(),
          });
        });
      });
    });
  return choices;
};

/** Choices matching every word typed ("bsn 2" finds "BSN 2-A"). */
export const matchSections = (choices, query) => {
  const words = String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return choices;
  return choices.filter((choice) => words.every((word) => choice.search.includes(word)));
};

const SectionUpdateModal = ({ show, status, onConfirmed }) => {
  const { handleLogout } = useContext(UserContext) || {};
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // The class list didn't load (a Try again button, not a dead end)
  const [loadFailed, setLoadFailed] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [sectionId, setSectionId] = useState("");
  const [query, setQuery] = useState("");
  // "My section isn't here" opened
  const [missingOpen, setMissingOpen] = useState(false);
  // With a class on record the student is first asked to keep it; "Change my class" opens the form
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (show) {
      setChanging(false);
      setQuery("");
      setMissingOpen(false);
    }
  }, [show]);

  const fetchOrgData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    try {
      const response = await api.get("/auth/user-organizational-info/");
      setOrgData(response.data);
    } catch (err) {
      console.error("Failed to load organizational options:", err);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const logOut = () => {
    if (handleLogout) handleLogout();
  };

  useEffect(() => {
    if (show) {
      fetchOrgData();
    }
  }, [show, fetchOrgData]);

  const choices = useMemo(() => sectionChoices(orgData?.available), [orgData]);
  const matches = useMemo(() => matchSections(choices, query), [choices, query]);
  const selected = choices.find((choice) => choice.id === String(sectionId));

  useEffect(() => {
    const current = orgData?.current?.section?.id;
    // The class on record starts selected, when it's one a student can pick
    if (current && choices.some((choice) => choice.id === String(current))) {
      setSectionId(String(current));
    }
  }, [orgData, choices]);

  const canSubmit = Boolean(selected && !submitting);

  const confirmSection = async (id) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post("/api/confirm-section/", {
        section_id: parseInt(id, 10),
      });
      toast.success("Saved. You can keep using ANNA.");
      onConfirmed?.(response.data);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        "Something went wrong while saving. Please try again.";
      setError(message);
      toast.error(message);
      // The class on record can't be kept (e.g. its department closed): pick another
      setChanging(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    confirmSection(selected.id);
  };

  const currentSchoolYear = status?.current_school_year || "this school year";
  const currentSection = status?.current_section;
  const asking = Boolean(currentSection?.section_id) && !changing;
  const currentClass = currentSection
    ? [
        currentSection.department_name,
        currentSection.organizational_level_name,
        currentSection.section_name,
      ].filter(Boolean).join(" · ")
    : "";

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      backdropClassName="section-update-backdrop"
      aria-labelledby="section-update-title"
    >
      <Modal.Header>
        <Modal.Title id="section-update-title">
          {asking ? "Check your class" : "Update your class"}
        </Modal.Title>
      </Modal.Header>

      {asking ? (
        <>
          <Modal.Body>
            <div className="am-stack">
              <div className="am-intro">
                <span className="am-intro-icon">
                  <Icon name="graduation-cap" size={28} />
                </span>
                <h3 className="am-intro-title">School year {currentSchoolYear}</h3>
                <p className="am-intro-text">Is this still your class?</p>
              </div>
              <div className="am-card">
                <span className="am-tile">
                  <Icon name="id-card" size={22} />
                </span>
                <span className="am-card-text">
                  <span className="am-card-meta">Your class</span>
                  <span className="am-card-title">{currentClass}</span>
                </span>
              </div>
              {error && (
                <p className="am-note is-danger" role="alert">
                  <Icon name="alert-triangle" size={18} />
                  <span>{error}</span>
                </p>
              )}
              {/* Always a way out (F17) */}
              <p className="su-logout-line">
                Not your account?{" "}
                <button type="button" className="am-link su-logout-link" onClick={logOut}>
                  Log out
                </button>
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer className="am-foot-split">
            <button
              type="button"
              className="am-btn is-soft"
              onClick={() => setChanging(true)}
              disabled={submitting}
            >
              <Icon name="pencil" size={18} />
              Change my class
            </button>
            <button
              type="button"
              className="am-btn is-primary"
              onClick={() => confirmSection(currentSection.section_id)}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="check" size={18} />
                  Yes, that&rsquo;s my class
                </>
              )}
            </button>
          </Modal.Footer>
        </>
      ) : (
      <>
      <Modal.Body>
        <div className="am-stack">
          <div className="am-intro">
            <span className="am-intro-icon">
              <Icon name="id-card" size={28} />
            </span>
            <h3 className="am-intro-title">School year {currentSchoolYear}</h3>
            <p className="am-intro-text">
              Find your section for this school year.
            </p>
          </div>

          {currentSection && (
            <p className="am-note">
              <Icon name="history" size={18} />
              <span>
                <b>On record:</b>{" "}
                {currentSection.department_name || "Department"} ·{" "}
                {currentSection.organizational_level_name || "Level"} ·{" "}
                {currentSection.section_name || "Section"}
              </span>
            </p>
          )}

          {error && (
            <p className="am-note is-danger" role="alert">
              <Icon name="alert-triangle" size={18} />
              <span>{error}</span>
            </p>
          )}

          {loading ? (
            <div className="am-intro" role="status">
              <Spinner animation="border" />
              <p className="am-intro-text">Loading the class list…</p>
            </div>
          ) : loadFailed ? (
            // F17: a failed load used to leave no way forward
            <div className="su-failed">
              <p className="am-note is-danger" role="alert">
                <Icon name="wifi-off" size={18} />
                <span>We couldn&rsquo;t load the class list. Check your internet, then try again.</span>
              </p>
              <button type="button" className="am-btn is-soft" onClick={fetchOrgData}>
                <Icon name="refresh" size={18} />
                Try again
              </button>
            </div>
          ) : (
            <form className="am-form" onSubmit={handleSubmit}>
              <label className="am-label" htmlFor="section-update-search">
                Your section
              </label>
              <label className="am-search su-search" htmlFor="section-update-search">
                <Icon name="search" size={20} />
                <input
                  id="section-update-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search, e.g. BSN 2-A or Grade 11"
                  autoComplete="off"
                  aria-controls="section-update-list"
                />
              </label>

              <div className="su-list" id="section-update-list" role="radiogroup" aria-label="Sections">
                {matches.slice(0, LIST_LIMIT).map((choice) => (
                  <label key={choice.id} className="am-choice su-choice">
                    <input
                      type="radio"
                      name="section-update-choice"
                      value={choice.id}
                      checked={choice.id === String(sectionId)}
                      onChange={() => setSectionId(choice.id)}
                    />
                    <span className="su-choice-text">
                      <b>{choice.name}</b>
                      <span>{choice.where}</span>
                    </span>
                  </label>
                ))}
                {matches.length === 0 && (
                  <p className="su-empty">
                    {choices.length === 0
                      ? "Your school hasn't added any sections yet."
                      : `No section matches "${query.trim()}".`}
                  </p>
                )}
                {matches.length > LIST_LIMIT && (
                  <p className="su-more">Keep typing to see the rest.</p>
                )}
              </div>

              {selected && (
                <p className="am-note is-success">
                  <Icon name="check-circle" size={18} />
                  <span>
                    <b>Selected:</b> {selected.name} · {selected.where}
                  </span>
                </p>
              )}

              {/* No staff notification yet: that's a pending decision (F17) */}
              <div className={`su-missing${missingOpen ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="su-missing-toggle"
                  aria-expanded={missingOpen}
                  onClick={() => setMissingOpen((open) => !open)}
                >
                  <Icon name="help-circle" size={18} />
                  My section isn&rsquo;t here
                  <Icon name="chevron-down" size={16} className="su-missing-chev" />
                </button>
                {missingOpen && (
                  <p className="su-missing-text">
                    Tell your guidance office your section so they can add it. Then come
                    back and pick it here. You can log out for now.
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </Modal.Body>

      {!loading && (
        <Modal.Footer className="am-foot-split">
          <button type="button" className="am-btn su-logout" onClick={logOut}>
            <Icon name="sign-out" size={18} />
            Log out
          </button>
          <button
            type="button"
            className="am-btn is-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              <>
                <Icon name="check" size={18} />
                Save and Continue
              </>
            )}
          </button>
        </Modal.Footer>
      )}
      </>
      )}
    </Modal>
  );
};

export default SectionUpdateModal;
